/** Thin wrappers over browser APIs. Every one degrades gracefully when unsupported. */

let wakeLock: WakeLockSentinel | null = null;
let wantWakeLock = false;

export const setWakeLock = async (on: boolean) => {
  wantWakeLock = on;
  try {
    if (on && !wakeLock && 'wakeLock' in navigator) {
      wakeLock = await navigator.wakeLock.request('screen');
      wakeLock.addEventListener('release', () => (wakeLock = null));
    } else if (!on && wakeLock) {
      await wakeLock.release();
      wakeLock = null;
    }
  } catch {
    wakeLock = null; // Denied (e.g. low battery). The timer still works.
  }
};

if (typeof document !== 'undefined') {
  // The OS drops wake locks when the page is hidden; re-acquire on return.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && wantWakeLock) void setWakeLock(true);
  });
}

let audio: AudioContext | null = null;

/** Must be called from a user gesture once (e.g. pressing Start) so iOS allows sound later. */
export const unlockAudio = () => {
  try {
    // Safari 16.4+: play even when the device is on silent. Rooms are quiet; the judge chose to turn sound on.
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) session.type = 'playback';
    audio ??= new AudioContext();
    if (audio.state === 'suspended') void audio.resume();
  } catch {
    /* no audio */
  }
};

type Tone = { at: number; freq: number; dur: number; to?: number };

const play = (tones: Tone[], volume: number) => {
  if (!audio || volume <= 0) return;
  const t0 = audio.currentTime + 0.02;
  // A compressor lets the tones sit near full scale without clipping, so they carry across a room.
  const comp = audio.createDynamicsCompressor();
  const master = audio.createGain();
  master.gain.value = volume;
  comp.connect(master).connect(audio.destination);
  for (const { at, freq, dur, to } of tones) {
    for (const [mult, level] of [[1, 0.9], [2, 0.25]] as const) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq * mult, t0 + at);
      if (to) osc.frequency.linearRampToValueAtTime(to * mult, t0 + at + dur);
      gain.gain.setValueAtTime(0.0001, t0 + at);
      gain.gain.exponentialRampToValueAtTime(level, t0 + at + 0.015);
      gain.gain.setValueAtTime(level, t0 + at + dur * 0.6);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
      osc.connect(gain).connect(comp);
      osc.start(t0 + at);
      osc.stop(t0 + at + dur + 0.05);
    }
  }
};

/** Each kind of alert sounds different, so you can tell them apart without looking. */
export const ALERT_SOUNDS = {
  /** Time's up: a falling two-tone alarm, three times (~2s). */
  end: [0, 0.7, 1.4].flatMap((at) => [{ at, freq: 988, dur: 0.3 }, { at: at + 0.32, freq: 740, dur: 0.34 }]),
  /** 10s or less: three quick high pips. */
  short: [0, 0.16, 0.32].map((at) => ({ at, freq: 1480, dur: 0.12 })),
  /** More than 10s: one rising chime. */
  long: [{ at: 0, freq: 660, to: 880, dur: 0.7 }],
} satisfies Record<string, Tone[]>;

export const alertKind = (secondsLeft: number): keyof typeof ALERT_SOUNDS => (secondsLeft === 0 ? 'end' : secondsLeft <= 10 ? 'short' : 'long');

/** Play the alert for a threshold (seconds left; 0 = time's up). */
export const timerBeep = (secondsLeft: number, volume = 0.8) => play(ALERT_SOUNDS[alertKind(secondsLeft)], volume);

export const requestPersistentStorage = async (): Promise<boolean | null> => {
  try {
    if (!navigator.storage?.persist) return null;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return null;
  }
};

export const storageStatus = async () => {
  try {
    const persisted = (await navigator.storage?.persisted?.()) ?? null;
    const est = (await navigator.storage?.estimate?.()) ?? null;
    return { persisted, usage: est?.usage ?? null, quota: est?.quota ?? null };
  } catch {
    return { persisted: null, usage: null, quota: null };
  }
};

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const downloadJson = (filename: string, data: unknown) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older Safari / insecure contexts.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
};

const fullscreenEl = () => document.fullscreenElement ?? (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement ?? null;

export const enterFullscreen = async (el: HTMLElement) => {
  try {
    if (fullscreenEl()) return;
    const anyEl = el as HTMLElement & { webkitRequestFullscreen?: () => void };
    if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
    else anyEl.webkitRequestFullscreen?.();
  } catch {
    /* CSS-only full-bleed still applies */
  }
};

export const exitFullscreen = async () => {
  try {
    if (!fullscreenEl()) return;
    const d = document as Document & { webkitExitFullscreen?: () => void };
    if (document.exitFullscreen) await document.exitFullscreen();
    else d.webkitExitFullscreen?.();
  } catch {
    /* ignore */
  }
};
